import { StorefrontApiClient, createStorefrontApiClient } from '@shopify/storefront-api-client';
import { useQuery } from '@applyboard/ui-utils';
import { ApplyShopConfig, ApplyShopStoreProductWithVariants } from './types';

const DEFAULT_SHOPIFY_BUY_API_VERSION = '2025-04';

export const PRODUCT_QUERY_BY_COLLECTION_TITLE = `
  query collections($first: Int, $collectionQuery: String) {
    collections(first: $first, query: $collectionQuery) {
        edges {
            node {
                id
                title
                handle
                products(first: $first) {
                    edges {
                      node {
                        id
                        title
                        handle
                        vendor
                        availableForSale
                        onlineStoreUrl
                        variants(first: $first) {
                            edges {
                                node {
                                    id
                                    title
                                    availableForSale
                                    compareAtPrice {
                                        amount
                                        currencyCode
                                    }
                                    price {
                                        amount
                                        currencyCode
                                    }
                                }
                            }
                        }
                      }
                    }
                }
            }
        }
    }
  }
`;

export const IndiaApplyShopStoreConfig: ApplyShopConfig = {
  domain: window.APPLYSHOP_STOREFRONT_DOMAIN || 'applyshop-india-dev.myshopify.com',
  storefrontAccessToken:
    window.APPLYSHOP_STOREFRONT_PUBLIC_TOKEN || '990e22154b18dd8d2fd85a58567bd8ee',
  apiVersion: DEFAULT_SHOPIFY_BUY_API_VERSION,
};

export const RowApplyShopStoreConfig: ApplyShopConfig = {
  domain: window.APPLYSHOP_ROW_STOREFRONT_DOMAIN || 'applyboard-test2015.myshopify.com',
  storefrontAccessToken:
    window.APPLYSHOP_ROW_STOREFRONT_PUBLIC_TOKEN || 'af1cb706d15c0c03c5fc138e65250981',
  apiVersion: DEFAULT_SHOPIFY_BUY_API_VERSION,
};
// This falls back to the RestOfWorldStoreFrontDomain until the new storefront is ready
// Link: row-retail-store.myshopify.com?
// Token ?
export const NepalApplyShopStoreConfig: ApplyShopConfig = {
  domain: window.APPLYSHOP_NEPAL_STOREFRONT_DOMAIN || 'applyboard-test2015.myshopify.com',
  storefrontAccessToken:
    window.APPLYSHOP_NEPAL_STOREFRONT_PUBLIC_TOKEN || 'af1cb706d15c0c03c5fc138e65250981',
  apiVersion: DEFAULT_SHOPIFY_BUY_API_VERSION,
};

export const DEFAULT_APPLYSHOP_CONFIG = RowApplyShopStoreConfig;

export function getApplyShopConfig(rpCountry: string, isNepalShopifyStoreEnabled: boolean) {
  if (rpCountry === 'IN') {
    return IndiaApplyShopStoreConfig;
  }
  if (rpCountry === 'NP' && isNepalShopifyStoreEnabled) {
    return NepalApplyShopStoreConfig;
  }

  return DEFAULT_APPLYSHOP_CONFIG;
}

function getShopifyClient(applyShopStoreConfig: ApplyShopConfig): StorefrontApiClient {
  return createStorefrontApiClient({
    storeDomain: applyShopStoreConfig.domain,
    publicAccessToken: applyShopStoreConfig.storefrontAccessToken,
    apiVersion: applyShopStoreConfig.apiVersion,
  });
}

export const useGetProducts = (
  {
    applyShopConfig,
    collectionName = 'Applyboard',
  }: { applyShopConfig: ApplyShopConfig; collectionName?: string },
  options: { enabled?: boolean } = {},
) => {
  const {
    data: products,
    isLoading: isProductsLoading,
    error,
  } = useQuery(
    [
      'getProductsV3',
      applyShopConfig,
      collectionName,
      {
        pkg: 'ab-vas-gic',
      },
    ],
    async () => {
      const client = getShopifyClient(applyShopConfig);
      if (!client) {
        return [];
      }

      const result = await client.request(PRODUCT_QUERY_BY_COLLECTION_TITLE, {
        variables: {
          first: 100,
          collectionQuery: `title:${collectionName}`,
        },
      });

      const collection = result?.data?.collections?.edges?.[0].node;
      const applyShopStoreProducts: ApplyShopStoreProductWithVariants[] = collection.products?.edges
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((product: any) => {
          const productDetails = product.node;
          const applyShopStoreProduct: ApplyShopStoreProductWithVariants = {
            isAvailable: productDetails.availableForSale,
            handle: productDetails.handle,
            onlineStoreUrl: productDetails.onlineStoreUrl,
            productId: productDetails.id,
            productTitle: productDetails.title,
            vendor: productDetails.vendor,
            variants: productDetails.variants?.edges
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((variant: any) => {
                const variatDetails = variant.node;
                const variantId = variatDetails.id.split('/').pop() || '';
                return {
                  applyShopCartLink: `https://${applyShopConfig.domain}/cart/${variantId}:1?storefront=true`,
                  variantId: variantId,
                  fullPrice: {
                    amount: `${variatDetails.compareAtPrice.amount}`,
                    currencyCode: variatDetails.compareAtPrice.currencyCode,
                  },
                  discountedPrice: {
                    amount: `${variatDetails.price.amount}`,
                    currencyCode: variatDetails.price.currencyCode,
                  },
                  country: variatDetails.title,
                  isAvailable: (variatDetails as any).availableForSale, // eslint-disable-line
                };
              })
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .sort((a: any, b: any) =>
                a.country.localeCompare(b.country, undefined, { sensitivity: 'base' }),
              ),
          };

          return applyShopStoreProduct;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .sort((a: any, b: any) =>
          a.productTitle.localeCompare(b.productTitle, undefined, { sensitivity: 'base' }),
        );

      return applyShopStoreProducts;
    },
    {
      enabled: options.enabled,
      refetchOnMount: false,
    },
  );

  return {
    products: products ?? [],
    isProductsLoading: isProductsLoading,
    productsError: error,
  };
};
